/**
 * Черновик правок шаблона: чистая часть (без React и сети).
 *
 * Шаблон версионирован (заголовок `If-Match`), поэтому на экране правки копятся
 * в черновике и уходят одной пачкой по кнопке «Сохранить ревизию»: сервер
 * применяет её целиком или не применяет вовсе и записывает ОДНУ ревизию.
 *
 * Черновик хранит ЖЕЛАЕМОЕ состояние, а не журнал действий: что работе
 * поменять и в каком порядке стоять этапам. Сравнение с тем, что сейчас на
 * сервере, даёт пачку (`batchOf`). Из этого три свойства: сдвиг на +7 и обратно
 * на −7 сам сводится к «нет правок»; отказ сервера ничего не записывает, и
 * черновик остаётся как был; после «шаблон изменился» его можно просто
 * наложить на свежую ревизию.
 *
 * Окна работ считает сервер (`plan_windows`); чтобы зависимые работы ехали
 * вместе с опорной ещё до сохранения, здесь есть клиентская копия расчёта —
 * `planWindows`. Её верность проверяется сверкой с окнами сервера (тест
 * `templateDraft.test.ts` и проба на живом шаблоне).
 *
 * Новая работа тоже живёт в черновике (`added`) и уходит в той же пачке
 * (`adds`): пока сервер её не создал, у неё отрицательный временный номер.
 * Она всегда стоит в списке этапа; её место в пачке выражено «после такой-то
 * работы» (`after_work_id`), потому что порядок (`orders`) сервер принимает
 * только со всеми работами этапа, а номеров новых у нас ещё нет.
 */

import type { TemplateBatchIn, TemplateBuilderStage, TemplateBuilderWork, TemplateTimingIn } from '../../../api/client';
import { addDays, applyRowMove, diffDays, type DayWindow, type OrderedGroup, type RowMove, type ShiftKind } from '../../ui/timeline/windowMath';

/** Срок работы в виде, общем для ответа сервера (`TimingOut`) и запроса (`TimingIn`). */
export interface TimingLike {
  start_kind: string;
  start_md?: string | null;
  start_year_offset?: number | null;
  start_after_id?: number | null;
  start_after_edge?: string | null;
  start_after_days?: number | null;
  end_kind: string;
  end_md?: string | null;
  end_year_offset?: number | null;
  duration_days?: number | null;
  approximate?: boolean;
}

/* ------------------------------------------------------------------ срок */

/**
 * Срок в каноническом виде — как его хранит сервер (`normalize_timing`):
 * поля, не относящиеся к форме срока, обнулены. Одинаковые по смыслу сроки
 * равны по значению, что нужно для «нет правок» и для завершения сохранения.
 */
export function canonicalTiming(t: TimingLike): Required<TimingLike> {
  const none = t.start_kind !== 'date' && t.start_kind !== 'after';
  const date = t.start_kind === 'date';
  const after = t.start_kind === 'after';
  const endDate = !none && t.end_kind === 'date';
  const endDuration = !none && t.end_kind === 'duration';
  return {
    start_kind: none ? 'none' : t.start_kind,
    start_md: date ? (t.start_md ?? null) : null,
    start_year_offset: date ? (t.start_year_offset ?? 0) : null,
    start_after_id: after ? (t.start_after_id ?? null) : null,
    start_after_edge: after ? (t.start_after_edge ?? null) : null,
    start_after_days: after ? (t.start_after_days ?? null) : null,
    end_kind: none ? 'none' : endDate ? 'date' : endDuration ? 'duration' : 'none',
    end_md: endDate ? (t.end_md ?? null) : null,
    end_year_offset: endDate ? (t.end_year_offset ?? 0) : null,
    duration_days: endDuration ? (t.duration_days ?? null) : null,
    approximate: !!t.approximate,
  };
}

export function sameTiming(a: TimingLike, b: TimingLike): boolean {
  return JSON.stringify(canonicalTiming(a)) === JSON.stringify(canonicalTiming(b));
}

/** Срок для запроса правки (допуск раннего срока не трогаем: сервер оставит прежний). */
export function timingIn(t: TimingLike): TemplateTimingIn {
  return canonicalTiming(t);
}

/* ------------------------------------------------- расчёт окон (копия сервера) */

export type WindowMap = Map<number, DayWindow | null>;

function resolveMd(md: string | null | undefined, offset: number | null | undefined, year: number): string | null {
  if (!md) return null;
  const [mm, dd] = md.split('-').map(Number);
  const y = year + (offset ?? 0);
  if (!mm || !dd) return null;
  const iso = `${y}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  // Несуществующий день (30 фев, 29 фев не високосного) — расчёт не удался.
  return addDays(iso, 0) === iso ? iso : null;
}

/**
 * Окна работ на год посадки — копия `work.plan_windows`: начало — дата или
 * край окна опорной работы плюс дни; конец — дата, начало плюс длительность
 * или отсутствует. Работа без начала окна не имеет. Цикл опор, опора вне
 * набора и конец раньше начала — `null` (расчёт не сходится; вызывающий
 * возвращается к окнам сервера).
 */
export function planWindows(timings: Map<number, TimingLike>, year: number): WindowMap | null {
  const result: WindowMap = new Map();
  const visiting = new Set<number>();
  let failed = false;

  const windowOf = (key: number): DayWindow | null => {
    if (result.has(key)) return result.get(key) ?? null;
    if (visiting.has(key)) {
      failed = true;
      return null;
    }
    visiting.add(key);
    const t = canonicalTiming(timings.get(key)!);
    let from: string | null = null;
    if (t.start_kind === 'date') {
      from = resolveMd(t.start_md, t.start_year_offset, year);
      if (from === null) failed = true;
    } else if (t.start_kind === 'after') {
      if (t.start_after_id === null || !timings.has(t.start_after_id)) failed = true;
      else {
        const anchor = windowOf(t.start_after_id);
        const edge = anchor ? (t.start_after_edge === 'start' ? anchor.from : (anchor.to ?? anchor.from)) : null;
        from = edge === null ? null : addDays(edge, t.start_after_days ?? 0);
      }
    }
    let to: string | null = null;
    if (from !== null) {
      if (t.end_kind === 'date') {
        to = resolveMd(t.end_md, t.end_year_offset, year);
        if (to === null) failed = true;
      } else if (t.end_kind === 'duration') {
        to = addDays(from, (t.duration_days ?? 1) - 1);
      }
      if (to !== null && to < from) failed = true;
    }
    visiting.delete(key);
    const win = from === null ? null : { from, to };
    result.set(key, win);
    return win;
  };

  for (const key of timings.keys()) windowOf(key);
  return failed ? null : result;
}

/* ------------------------------------------------------------- перетаскивание */

export type WorkDrag = 'free' | 'whole' | 'fixed';

/**
 * Как работу можно двигать на шкале. Работа с датами — целиком и за края
 * (метка без конца — только целиком, это решает шкала). Отсчитываемая от
 * другой — только целиком: меняется смещение. Остальное (без окна, смещение
 * с датой конца) на шкале не правится — форма в ящике.
 */
export function dragOfTiming(t: TimingLike, hasWindow: boolean): WorkDrag {
  if (!hasWindow) return 'fixed';
  const c = canonicalTiming(t);
  if (c.start_kind === 'date') return 'free';
  if (c.start_kind === 'after' && c.end_kind !== 'date') return 'whole';
  return 'fixed';
}

function mdOf(iso: string, year: number): { md: string; offset: number } | null {
  const md = iso.slice(5, 10);
  const offset = Number(iso.slice(0, 4)) - year;
  // 29 февраля без года записать нельзя (сервер откажет), как и год вне «до / посадки / после».
  if (md === '02-29' || offset < -1 || offset > 1) return null;
  return { md, offset };
}

/**
 * Срок после того, как окно на шкале перетащили из `before` в `after`.
 * `null` — так задать срок нельзя (29 февраля, год вне допустимых, форма срока
 * не двигается на шкале).
 */
export function timingAfterShift(t: TimingLike, year: number, before: DayWindow, after: DayWindow, kind: ShiftKind): TemplateTimingIn | null {
  const c = canonicalTiming(t);
  if (c.start_kind === 'date') {
    const start = mdOf(after.from, year);
    if (!start) return null;
    const next = { ...c, start_md: start.md, start_year_offset: start.offset };
    if (c.end_kind === 'date') {
      if (after.to === null) return null;
      const end = mdOf(after.to, year);
      if (!end) return null;
      return { ...next, end_md: end.md, end_year_offset: end.offset };
    }
    if (c.end_kind === 'duration') {
      if (after.to === null) return null;
      return { ...next, duration_days: diffDays(after.from, after.to) + 1 };
    }
    return next;
  }
  if (c.start_kind === 'after' && c.end_kind !== 'date' && kind === 'move') {
    return { ...c, start_after_days: (c.start_after_days ?? 0) + diffDays(before.from, after.from) };
  }
  return null;
}

/* ----------------------------------------------------------------- черновик */

/** Правка одной работы: только то, что поменяли. */
export interface WorkEdit {
  label?: string;
  is_required?: boolean;
  catalog_work_id?: number;
  timing?: TemplateTimingIn;
}

/** Новая работа, которой сервер ещё не создал: временный номер отрицателен. */
export interface DraftAdd {
  /** Временный номер (< 0): по нему работа живёт на шкале, в порядке и в ящике. */
  id: number;
  stageId: number;
  label: string;
  isRequired: boolean;
  catalogWorkId: number;
  /** Название вида из каталога — подпись строки, пока своей подписи нет. */
  kindName: string;
  category: TemplateBuilderWork['category'];
  timing: TemplateTimingIn;
}

export interface Draft {
  works: Record<number, WorkEdit>;
  /** Желаемый порядок работ в затронутых этапах (id этапа → id работ). */
  order: Record<number, number[]>;
  /** Работы на удаление. */
  removed: number[];
  /** Новые работы: уйдут в пачке как `adds`. */
  added: DraftAdd[];
}

export const EMPTY_DRAFT: Draft = { works: {}, order: {}, removed: [], added: [] };

/** Работа, которой ещё нет на сервере (временный номер). */
export const isNewId = (id: number): boolean => id < 0;

export interface EffWork {
  work: TemplateBuilderWork;
  /** Этап, в котором работа стоит с учётом черновика. */
  stageId: number;
  label: string;
  isRequired: boolean;
  catalogWorkId: number;
  timing: TimingLike;
  /** Поля или срок отличаются от сервера (у новой работы — всегда). */
  edited: boolean;
  removed: boolean;
  /** Новая работа черновика: на сервере её ещё нет. */
  added: boolean;
}

export interface EffStage {
  stage: TemplateBuilderStage;
  works: EffWork[];
}

/** Новая работа в форме, которой её рисуют экран и ящик (как будто сервер её уже отдал). */
function addedAsWork(add: DraftAdd): TemplateBuilderWork {
  return {
    id: add.id,
    stage_id: add.stageId,
    order_no: Number.MAX_SAFE_INTEGER + add.id,
    label: add.label,
    name: add.label.trim() || add.kindName,
    catalog_work_id: add.catalogWorkId,
    catalog_code: '',
    category: add.category,
    is_required: add.isRequired,
    irrigation_no: null,
    timing: { ...canonicalTiming(add.timing), early_tolerance_days: 0 },
    timing_label: '',
    window_from: null,
    window_to: null,
  };
}

/** Работы шаблона и новые работы черновика. */
function allWorks(stages: TemplateBuilderStage[], draft?: Draft): Map<number, TemplateBuilderWork> {
  const map = new Map<number, TemplateBuilderWork>();
  for (const stage of stages) for (const work of stage.works) map.set(work.id, work);
  for (const add of draft?.added ?? []) map.set(add.id, addedAsWork(add));
  return map;
}

function editedFields(work: TemplateBuilderWork, edit: WorkEdit | undefined): { label: boolean; required: boolean; kind: boolean; timing: boolean } {
  return {
    label: edit?.label !== undefined && edit.label.trim() !== work.label.trim(),
    required: edit?.is_required !== undefined && edit.is_required !== work.is_required,
    kind: edit?.catalog_work_id !== undefined && edit.catalog_work_id !== work.catalog_work_id,
    timing: edit?.timing !== undefined && !sameTiming(edit.timing, work.timing),
  };
}

/** Желаемые списки работ по этапам: порядок черновика поверх порядка сервера. */
export function stageOrder(stages: TemplateBuilderStage[], draft: Draft): Map<number, number[]> {
  const known = allWorks(stages, draft);
  const stageIds = new Set(stages.map((s) => s.id));
  const claimed = new Map<number, number>();
  for (const [stageId, ids] of Object.entries(draft.order)) {
    // Список удалённого этапа (его удалили, пока черновик лежал) ничего не занимает.
    if (!stageIds.has(Number(stageId))) continue;
    for (const id of ids) if (known.has(id) && !claimed.has(id)) claimed.set(id, Number(stageId));
  }
  const out = new Map<number, number[]>();
  for (const stage of stages) {
    const listed = (draft.order[stage.id] ?? []).filter((id) => claimed.get(id) === stage.id);
    // Новые работы стоят за теми, что уже есть на сервере, пока порядок черновика не говорит иначе.
    const base = [...stage.works.map((w) => w.id), ...draft.added.filter((a) => a.stageId === stage.id).map((a) => a.id)];
    const rest = base.filter((id) => !claimed.has(id) && !listed.includes(id));
    out.set(stage.id, [...listed, ...rest]);
  }
  return out;
}

/** Этапы и работы с учётом черновика (порядок, этап, поля, срок). */
export function effectiveStages(stages: TemplateBuilderStage[], draft: Draft): EffStage[] {
  const known = allWorks(stages, draft);
  const order = stageOrder(stages, draft);
  const removed = new Set(draft.removed);
  return stages.map((stage) => ({
    stage,
    works: (order.get(stage.id) ?? []).map((id) => {
      const work = known.get(id)!;
      const isNew = isNewId(id);
      // Правки новой работы лежат в самой записи `added`, а не в `works`.
      const edit = isNew ? undefined : draft.works[id];
      const fields = editedFields(work, edit);
      return {
        work,
        stageId: stage.id,
        label: edit?.label !== undefined ? edit.label : work.label,
        isRequired: edit?.is_required ?? work.is_required,
        catalogWorkId: edit?.catalog_work_id ?? work.catalog_work_id,
        timing: edit?.timing ?? work.timing,
        edited: isNew || fields.label || fields.required || fields.kind || fields.timing,
        removed: removed.has(id),
        added: isNew,
      };
    }),
  }));
}

export interface DraftSummary {
  /** Новые работы, которых на сервере ещё нет. */
  added: number[];
  /** Работы с правкой полей или срока. */
  edited: number[];
  /** Этапы, чей состав или порядок отличается от сервера. */
  reordered: number[];
  removed: number[];
  total: number;
}

export function summarize(stages: TemplateBuilderStage[], draft: Draft): DraftSummary {
  const known = allWorks(stages);
  const order = stageOrder(stages, draft);
  // Новая работа, чей этап удалили, пока черновик лежал, никуда не встала — в счёт не идёт.
  const placed = new Set<number>();
  for (const ids of order.values()) for (const id of ids) placed.add(id);
  const added = draft.added.filter((a) => placed.has(a.id)).map((a) => a.id);
  const removed = draft.removed.filter((id) => known.has(id));
  const edited = Object.keys(draft.works)
    .map(Number)
    .filter((id) => {
      const work = known.get(id);
      if (!work || removed.includes(id)) return false;
      const f = editedFields(work, draft.works[id]);
      return f.label || f.required || f.kind || f.timing;
    });
  const reordered = stages
    .filter((stage) => {
      const now = stage.works.map((w) => w.id);
      // Новые работы порядка серверных не меняют: их место выражено «после такой-то».
      const want = (order.get(stage.id) ?? []).filter((id) => !isNewId(id));
      return now.length !== want.length || now.some((id, i) => id !== want[i]);
    })
    .map((s) => s.id);
  return { added, edited, reordered, removed, total: added.length + edited.length + reordered.length + removed.length };
}

export function isDirty(stages: TemplateBuilderStage[], draft: Draft): boolean {
  return summarize(stages, draft).total > 0;
}

/** Окна работ: по черновику, а если расчёт не сошёлся — как их отдал сервер. */
export function draftWindows(layout: EffStage[], year: number): WindowMap {
  const timings = new Map<number, TimingLike>();
  for (const { works } of layout) for (const w of works) timings.set(w.work.id, w.timing);
  const planned = planWindows(timings, year);
  if (planned) return planned;
  const fallback: WindowMap = new Map();
  for (const { works } of layout) {
    for (const w of works) fallback.set(w.work.id, w.work.window_from ? { from: w.work.window_from, to: w.work.window_to } : null);
  }
  return fallback;
}

/** Окна, какими они станут, если одной работе поменять срок (для живого предпросмотра). */
export function windowsWithTiming(layout: EffStage[], year: number, workId: number, timing: TimingLike): WindowMap | null {
  const timings = new Map<number, TimingLike>();
  for (const { works } of layout) for (const w of works) timings.set(w.work.id, w.work.id === workId ? timing : w.timing);
  return planWindows(timings, year);
}

/* ----------------------------------------------------------------- правки */

export function setWorkEdit(draft: Draft, id: number, patch: WorkEdit): Draft {
  if (isNewId(id)) {
    // Новая работа правится прямо в записи черновика: «как было» у неё нет.
    return {
      ...draft,
      added: draft.added.map((a) =>
        a.id !== id
          ? a
          : {
              ...a,
              ...(patch.label !== undefined ? { label: patch.label } : {}),
              ...(patch.is_required !== undefined ? { isRequired: patch.is_required } : {}),
              ...(patch.catalog_work_id !== undefined ? { catalogWorkId: patch.catalog_work_id } : {}),
              ...(patch.timing !== undefined ? { timing: patch.timing } : {}),
            },
      ),
    };
  }
  return { ...draft, works: { ...draft.works, [id]: { ...draft.works[id], ...patch } } };
}

/** Следующий временный номер новой работы: отрицательный, не занятый другой новой. */
export function nextAddId(draft: Draft): number {
  return Math.min(0, ...draft.added.map((a) => a.id)) - 1;
}

/** Новая работа в черновик: встаёт в конец этапа; возвращает черновик и её временный номер. */
export function addWork(draft: Draft, add: Omit<DraftAdd, 'id'>): { draft: Draft; id: number } {
  const id = nextAddId(draft);
  return { draft: { ...draft, added: [...draft.added, { ...add, id }] }, id };
}

/** Перенос строки: порядок затронутых этапов записывается в черновик целиком. */
export function moveWork(stages: TemplateBuilderStage[], draft: Draft, move: RowMove): Draft {
  const order = stageOrder(stages, draft);
  const groups: OrderedGroup[] = stages.map((s) => ({ id: String(s.id), ids: (order.get(s.id) ?? []).map(String) }));
  const next = applyRowMove(groups, move);
  const nextOrder = { ...draft.order };
  next.forEach((group, i) => {
    if (group.ids.join() !== groups[i]!.ids.join()) nextOrder[Number(group.id)] = group.ids.map(Number);
  });
  return { ...draft, order: nextOrder };
}

export function removeWork(draft: Draft, id: number): Draft {
  if (isNewId(id)) {
    // Новую работу сервер не знает: «удалить» значит просто забыть её.
    const order: Record<number, number[]> = {};
    for (const [stageId, ids] of Object.entries(draft.order)) order[Number(stageId)] = ids.filter((x) => x !== id);
    return { ...draft, added: draft.added.filter((a) => a.id !== id), order };
  }
  return draft.removed.includes(id) ? draft : { ...draft, removed: [...draft.removed, id] };
}

export function restoreWork(draft: Draft, id: number): Draft {
  return { ...draft, removed: draft.removed.filter((x) => x !== id) };
}

/* ------------------------------------------------------------- сохранение */

/** Тело `POST …/batch`: весь черновик одним запросом, одна ревизия (agroai-b48.1). */
export type DraftBatch = TemplateBatchIn;

/**
 * Пачка правок, которая сводит сервер к черновику; `null` — делать нечего.
 *
 * Сервер применяет её в одной транзакции и в таком порядке: правки работ вместе
 * с переносом между этапами (работа встаёт в конец нового этапа), порядок внутри
 * этапов, в самом конце — удаления (до этого удаляемая работа остаётся в списке
 * порядка этапа, поэтому в `orders` она есть). Порядок этапа попадает в пачку,
 * только если после переносов состав и порядок всё ещё отличаются от желаемых.
 */
export function batchOf(stages: TemplateBuilderStage[], draft: Draft): DraftBatch | null {
  const known = allWorks(stages, draft);
  const removed = new Set(draft.removed.filter((id) => known.has(id) && !isNewId(id)));
  const order = stageOrder(stages, draft);
  const effectiveStage = new Map<number, number>();
  for (const [stageId, ids] of order) for (const id of ids) effectiveStage.set(id, stageId);

  const edits: DraftBatch['edits'] = [];
  const settled = new Map<number, number[]>(stages.map((stage) => [stage.id, stage.works.map((w) => w.id)]));
  for (const work of known.values()) {
    if (isNewId(work.id) || removed.has(work.id)) continue;
    const edit = draft.works[work.id];
    const f = editedFields(work, edit);
    const target = effectiveStage.get(work.id) ?? work.stage_id;
    const item: DraftBatch['edits'][number] = { work_id: work.id };
    if (f.label) item.label = edit!.label!.trim();
    if (f.required) item.is_required = edit!.is_required!;
    if (f.kind) item.catalog_work_id = edit!.catalog_work_id!;
    if (f.timing) item.timing = timingIn(edit!.timing!);
    if (target !== work.stage_id) {
      item.stage_id = target;
      settled.set(work.stage_id, (settled.get(work.stage_id) ?? []).filter((id) => id !== work.id));
      settled.set(target, [...(settled.get(target) ?? []), work.id]);
    }
    if (Object.keys(item).length > 1) edits.push(item);
  }

  // Новые работы, вставшие в существующие этапы (этап удалили — работа никуда не встала).
  const addById = new Map(draft.added.map((a) => [a.id, a]));
  const stagesWithAdds = new Set<number>();
  for (const [stageId, ids] of order) if (ids.some((id) => addById.has(id))) stagesWithAdds.add(stageId);

  const orders: DraftBatch['orders'] = [];
  for (const stage of stages) {
    const now = settled.get(stage.id) ?? [];
    // Порядок серверных работ: новых в списке порядка быть не может (их номеров ещё нет).
    const want = (order.get(stage.id) ?? []).filter((id) => !isNewId(id));
    if (now.length === want.length && now.every((id, i) => id === want[i])) continue;
    if (stagesWithAdds.has(stage.id)) {
      // Сервер принимает порядок этапа только со всеми его работами, включая новые, которых он ещё не создал.
      // Тот же порядок выражается цепочкой «поставить после предыдущей» — она идёт до новых работ.
      want.forEach((id, i) => edits.push({ work_id: id, stage_id: stage.id, after_work_id: i === 0 ? 0 : want[i - 1]! }));
      continue;
    }
    orders.push({ stage_id: stage.id, ordered_ids: want });
  }

  // Место новой работы — «после ближайшей серверной выше неё» (0 — в начало этапа). Работы с одной опорой
  // кладём с конца: каждая встаёт сразу за опорой, и итоговый порядок сходится с тем, что видит экран.
  const adds: DraftBatch['adds'] = [];
  for (const [stageId, ids] of order) {
    let after = 0;
    const placed: Array<{ add: DraftAdd; after: number }> = [];
    for (const id of ids) {
      const add = addById.get(id);
      if (add) placed.push({ add, after });
      else after = id;
    }
    for (const { add, after: anchor } of placed.reverse()) {
      adds.push({
        stage_id: stageId,
        catalog_work_id: add.catalogWorkId,
        label: add.label.trim(),
        is_required: add.isRequired,
        timing: timingIn(add.timing),
        after_work_id: anchor,
      });
    }
  }

  const removes = [...removed];
  if (edits.length === 0 && adds.length === 0 && orders.length === 0 && removes.length === 0) return null;
  return { edits, adds, orders, removes };
}
